import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./CustomerCreditSummary.css";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { useSnackbar } from "../hooks/SnackbarProvider";
import {
  getCreditOrderDetails,
  getSettlementDetails,
  getCreditPaymentStatus,
  settlePayment,
} from "../../../../config/Services";
import BillDrawer from "../OrdersV2/Component/BillDrawer";
import { useNavigate, useParams } from "react-router-dom";
import { customEventTrigger } from "../../../../analytics";
import PaymentModal from "../all-products/components/twoWaykeyboardPaymentPage/components/paymentModal";
import cashNew from "../../../../assets/images/cash-new.png";
import cashNewSelected from "../../../../assets/images/cash-new-selected.png";
import cardNew from "../../../../assets/images/card-new.png";
import cardNewSelected from "../../../../assets/images/card-new-selected.png";
import upiNew from "../../../../assets/images/upi-new.png";
import upiNewSelected from "../../../../assets/images/upi-new-selected.png";
import splitNew from "../../../../assets/images/split-new.png";
import splitNewSelected from "../../../../assets/images/split-new-selected.png";

/* ─── Badge Helper ─── */
const STATUS_MAP = {
  DEBITED: { label: "Outstanding", cls: "ccs-badge-outstanding" },
  SETTLED: { label: "Settled", cls: "ccs-badge-settled" },
  REFUNDED: { label: "Refunded", cls: "ccs-badge-refunded" },
  CREDITED: { label: "Settled", cls: "ccs-badge-settled" },
};

const Badge = ({ status }) => {
  const mapped = STATUS_MAP[status?.toUpperCase()] ?? { label: status, cls: "" };
  return <span className={`ccs-badge ${mapped?.cls}`}>{mapped?.label}</span>;
};

/* ─── Format helpers ─── */
const formatCurrency = (amount) => {
  if (amount == null) return "—";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "Pending";
  const utcDateStr = dateStr.endsWith("Z") ? dateStr : (dateStr.includes("T") ? `${dateStr}Z` : `${dateStr} UTC`);
  const d = new Date(utcDateStr);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return "—";
  const utcDateStr = dateStr.endsWith("Z") ? dateStr : (dateStr.includes("T") ? `${dateStr}Z` : `${dateStr} UTC`);
  const d = new Date(utcDateStr);
  return d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ─── Skeleton Row ─── */
const SKELETON_WIDTHS = [55, 70, 40, 65, 40, 60, 50, 65];
const SkeletonRow = ({ cols, gridClass }) => (
  <div className={`ccs-grid ${gridClass} ccs-grid-row ccs-skeleton-row`}>
    {SKELETON_WIDTHS.slice(0, cols)?.map((w, i) => (
      <div key={i} className="ccs-grid-cell">
        <div className="ccs-skeleton" style={{ width: `${w}%` }} />
      </div>
    ))}
  </div>
);

/* ─── Empty State ─── */
const EmptyState = ({ gridClass, cols, message }) => (
  <div className={`ccs-grid ${gridClass} ccs-grid-row`} style={{ gridTemplateColumns: "1fr" }}>
    <div className="ccs-grid-cell ccs-empty-cell" style={{ gridColumn: `1 / ${cols + 1}` }}>
      {message}
    </div>
  </div>
);

/* ─── Pagination ─── */
const Pagination = ({ page, totalPages, totalResults, pageSize, onPageChange }) => {
  if (totalPages <= 1 && totalResults <= pageSize) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalResults);
  return (
    <div className="ccs-pagination">
      <span className="ccs-pagination-info">
        {totalResults > 0 ? `${from}–${to} of ${totalResults}` : "0 results"}
      </span>
      <div className="ccs-pagination-btns">
        <button
          className="ccs-page-btn"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        {Array.from({ length: Math.min(totalPages, 5) })?.map((_, i) => {
          let pageNum = i;
          if (totalPages > 5) {
            const start = Math.max(0, Math.min(page - 2, totalPages - 5));
            pageNum = start + i;
          }
          return (
            <button
              key={pageNum}
              className={`ccs-page-btn ccs-page-num ${pageNum === page ? "active" : ""}`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum + 1}
            </button>
          );
        })}
        <button
          className="ccs-page-btn"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ─── Grid Headers ─── */
const ORDER_COLS = [
  "Date",
  "Order ID",
  "Item Count",
  "Cashier Name",
  "Invoice Aging",
  "Payment Status",
  "Amount",
  "Payment Date",
];
const SETTLEMENT_COLS = [
  "Settlement Id",
  "Amount Paid",
  "Settlement Date",
  "Payment Method",
];

const PAGE_SIZE = 8;

/* ─── Main Component ─── */
const CustomerCreditSummary = () => {
  const [activeTab, setActiveTab] = useState("all-orders");
  const [loading, setLoading] = useState(false);

  const pathParams = useParams();

  // Orders state
  const [orderMeta, setOrderMeta] = useState(null);
  const [orderLogs, setOrderLogs] = useState([]);
  const [orderPage, setOrderPage] = useState(0);
  const [orderTotalPages, setOrderTotalPages] = useState(0);
  const [orderTotalResults, setOrderTotalResults] = useState(0);

  // Settlements state
  const [settlementMeta, setSettlementMeta] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [settlementPage, setSettlementPage] = useState(0);
  const [settlementTotalPages, setSettlementTotalPages] = useState(0);
  const [settlementTotalResults, setSettlementTotalResults] = useState(0);

  const [viewBillOpen, setViewBillOpen] = useState(false);

  const [selectedSettlement, setSelectedSettlement] = useState(null);

  const showSnackbar = useSnackbar();

  const navigate = useNavigate();

  // Derive header data: prefer order meta, fallback to settlement meta
  const meta = orderMeta || settlementMeta;

  // ── localStorage values (same pattern as RestaurantReservationUI) ──
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const sessionId = localStorage.getItem("sessionId");
  const licenseId = localStorage.getItem("licenseId");
  const licenseName = localStorage.getItem("licenseName");
  const machineId = localStorage.getItem("machineId");
  const storeMode = localStorage.getItem("storeMode");
  const retailUserLocalStorageData = useMemo(
    () => JSON.parse(localStorage.getItem("retailUserDetails") || "{}"),
    []
  );

  // ── Payment modal state ──
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [tpPaymentMethod, setTpPaymentMethod] = useState("CASH");
  const [tpEnterAmount, setTpEnterAmount] = useState("");
  const tpEnterAmountRef = useRef(null);
  const [tpTotal, setTpTotal] = useState(0);
  const [tpPaymentLoader, setTpPaymentLoader] = useState(false);
  const [tpPaymentSuccess, setTpPaymentSuccess] = useState(false);
  const [tpPaymentFailed, setTpPaymentFailed] = useState(false);
  const [tpPaymentCancel, setTpPaymentCancel] = useState(false);
  const [tpNewLoader, setTpNewLoader] = useState(false);
  const [tpSplitActive, setTpSplitActive] = useState(false);
  const [tpPartialStatus, setTpPartialStatus] = useState(false);
  const [tpAmountPaid, setTpAmountPaid] = useState("");
  const [tpAmountDue, setTpAmountDue] = useState("");
  const [tpCashChange, setTpCashChange] = useState(false);
  const [tpManual, setTpManual] = useState(false);
  const [tpCancelLoader, setTpCancelLoader] = useState(false);
  const [tpPayLoader, setTpPayLoader] = useState(false);
  const [tpModalLoader, setTpModalLoader] = useState(false);
  const [tpNewId, setTpNewId] = useState(null);

  // ── Open payment modal ──
  const handleOpenPayment = () => {
    // Guard: outStandingAmount can be 0 but not null/undefined
    if (meta?.outStandingAmount == null || meta.outStandingAmount <= 0) {
      showSnackbar("No outstanding balance to pay", "warning");
      return;
    }
    customEventTrigger("pos_payment_page"); // missing from your current code
    tpResetValues(); // reset everything before opening
    setTpTotal(meta?.outStandingAmount);
    setTpPaymentMethod("CASH");
    setPaymentModalOpen(true);
  };

  // ── Close payment modal ──
  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setTpPaymentMethod("CASH");
    setTpEnterAmount("");
    setTpSplitActive(false);
    setTpPartialStatus(false);
    setTpPaymentLoader(false);
    setTpPaymentSuccess(false);
    setTpPaymentFailed(false);
    setTpAmountPaid("");
    setTpAmountDue("");
    setTpCashChange(false);
    setTpPayLoader(false);
    setTpNewId(null);
  };

  // ── Reset after success — just close ──
  const tpResetValues = () => {
    setTpSplitActive(false);
    setTpEnterAmount("");
    setTpPayLoader(false);
    setTpCashChange(false);
    setTpPartialStatus(false);
    setTpAmountPaid("");
    setTpAmountDue("");
    setTpNewLoader(false);
    setTpModalLoader(false);
    setTpPaymentLoader(false);
    setTpPaymentSuccess(false);
    setTpPaymentFailed(false);
    setTpPaymentCancel(false);
    setTpManual(false);
    setTpCancelLoader(false);
    setTpNewId(null);
  };

  // ── Split helper ──
  const tpHandleSplitPayment = () => {
    setTpPaymentMethod("CASH");
    setTpSplitActive(true);
    setTpEnterAmount("");
  };

  const tpHandleSplitInputChange = (e) => {
    setTpEnterAmount(e.target.value);
  };

  // ── Build payload (mirrors CreditOrderDetailsPage.settlePaymentFun exactly) ──
  const buildSettlePayload = (settlementStatus = "PENDING") => ({
    customerId: meta?.customerId || null,
    amount: tpPaymentMethod === "CASH" ? tpEnterAmount : tpTotal,
    locationId: locId || "",
    orgId: orgId || "",
    sessionId: sessionId || null,
    licenseId: licenseId || null,
    licenseName: licenseName || null,
    machineCode: machineId || null,
    accountNoteId: meta?.accountNoteId || null,
    paymentMethod: tpPaymentMethod,
    // paymentMode: machineId ? "ONLINE" : "OFFLINE",
    paymentMode: storeMode,
    updatedByName: retailUserLocalStorageData?.firstName || null,
    settlementStatus,
    createdBy: retailUserLocalStorageData?.uidx || null,
  });

  // ── Terminal polling — returns a promise that resolves when COMPLETED ──
  const callPaymentStatus = (transId, currentRetry, settlement, setID) => {
    const maxRetries = 95;
    let retry = currentRetry || 0;

    getCreditPaymentStatus(transId, settlement, setID)
      .then((res) => {
        const status = res?.data?.data?.paymentResponseModel?.paymentStatus;

        if (status === "PAYMENT_INITIATED" && retry < maxRetries) {
          retry++;
          showSnackbar(status, "success");
          setTpPaymentLoader(true); // paymentLoadingModal → paymentLoader in RestaurantPaymentModal
          setTpPaymentSuccess(false);
          setTimeout(() => callPaymentStatus(transId, retry, settlement, setID), 2000 + retry);
          if (retry === maxRetries) setTpPaymentFailed(true);
        } else if (status === "COMPLETED") {
          showSnackbar(status, "success");
          // Finalize settlement record with COMPLETED status
          settlePayment(buildSettlePayload("COMPLETED"));
          closePaymentModal();
          tpResetValues();
          fetchAllOrdersFn(0);
          navigate("/credit-order-completed", {
            state: {
              amount: tpPaymentMethod === "CASH" ? tpEnterAmount : tpTotal,
              paymentMethod: tpPaymentMethod,
              settlementId: setID,
              customerName: meta?.customerName,
              customerMobile: meta?.mobileNumber,
            },
          });
        } else if (status === "PAYMENT_CANCELLED" || status === "CANCELLED") {
          setTpPaymentLoader(false);
          setTpCancelLoader(false);
          debugger;
          showSnackbar(status, "error");
        }
      })
      .catch((err) => {
        console.log(err, "this is the error");
        debugger;
        showSnackbar(err?.response?.data?.message || err?.message, "error");
      });
  };

  // ── Main pay handler (mirrors CreditOrderDetailsPage.settlePaymentFun exactly) ──
  const tpCheckPayment = async () => {
    setTpModalLoader(true);

    if (tpPaymentMethod === "CASH" && (!tpEnterAmount || Number(tpEnterAmount) < 1)) {
      showSnackbar("You need to pay at least 1 Rupee to process this order", "error");
      setTpModalLoader(false);
      return;
    }

    const payload = buildSettlePayload("PENDING");

    try {
      const res = await settlePayment(payload);

      if (res?.data?.data?.es === 0) {
        // CARD/UPI + physical terminal → poll for status
        // if (machineId && tpPaymentMethod !== "CASH") {
        if (tpPaymentMethod !== "CASH" && storeMode === "ONLINE") {
          if (!res?.data?.data?.transactionCode) {
            showSnackbar("Transaction code not found", "error");
            setTpModalLoader(false);
            return;
          }
          // now the source of truth is storeMode && cartStoreMode
          await callPaymentStatus(
            res?.data?.data?.transactionCode,
            0,
            true,
            res?.data?.data?.settlementId
          ).catch(() => {});
          setTpModalLoader(false);
          closePaymentModal();
          tpResetValues();
          fetchAllOrdersFn(0);
          navigate("/credit-order-completed", {
            state: {
              amount: tpPaymentMethod === "CASH" ? tpEnterAmount : tpTotal,
              paymentMethod: tpPaymentMethod,
              settlementId: res?.data?.data?.settlementId,
              customerName: meta?.customerName,
              customerMobile: meta?.mobileNumber,
            },
          });
          return;
        }

        // CASH or OFFLINE or No Terminal → success immediately
        closePaymentModal();
        tpResetValues();
        fetchAllOrdersFn(0);
        navigate("/credit-order-completed", {
          state: {
            amount: tpEnterAmount,
            paymentMethod: tpPaymentMethod,
            settlementId: res?.data?.data?.settlementId,
            customerName: meta?.customerName,
            customerMobile: meta?.mobileNumber,
          },
        });
        setTpModalLoader(false);
      } else {
        setTpModalLoader(false);
        showSnackbar(res?.data?.data?.message || "Something went wrong, please try again", "error");
      }
    } catch (err) {
      showSnackbar(err?.message || err?.response?.data?.message || "Something went wrong", "error");
      setTpModalLoader(false);
    }
  };

  // ── Partial complete — same settlePayment call for subsequent split captures ──
  const tpPartialCompletePayment = () => {
    tpCheckPayment();
  };

  /* ── Fetch All Orders ── */
  const fetchAllOrdersFn = useCallback(
    async (page = 0) => {
      setLoading(true);
      try {
        const payload = { accountNoteId: pathParams?.accountNoteId, page, pageSize: PAGE_SIZE };
        const response = await getCreditOrderDetails(payload);
        const d = response?.data?.data;

        if (d?.es !== 0) throw new Error("failed");
        setOrderMeta(d?.accountNote ?? null);
        setOrderLogs(d?.accountNote?.accountLogs ?? []);
        setOrderPage(d?.page ?? 0);
        // setOrderTotalPages(d?.totalPages ?? 0);
        setOrderTotalPages(Math.ceil((d?.totalResults ?? 0) / PAGE_SIZE));
        setOrderTotalResults(d?.totalResults ?? 0);
      } catch (err) {
        showSnackbar("Error fetching orders", "error");
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  /* ── Fetch All Settlements ── */
  const fetchAllSettlementFn = useCallback(
    async (page = 0) => {
      setLoading(true);
      try {
        const payload = { accountNoteId: pathParams?.accountNoteId, page, pageSize: PAGE_SIZE };
        const response = await getSettlementDetails(payload);
        const d = response?.data?.data;
        if (d?.es !== 0) throw new Error("failed");
        setSettlementMeta(d?.accountNote ?? null);
        setSettlements(d?.settlements ?? []);
        setSettlementPage(d?.page ?? 0);
        // setSettlementTotalPages(d?.totalPages ?? 0);
        setSettlementTotalPages(Math.ceil((d?.totalResults ?? 0) / PAGE_SIZE));
        setSettlementTotalResults(d?.totalResults ?? 0);
      } catch (err) {
        showSnackbar("Error fetching settlements", "error");
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  useEffect(() => {
    if (activeTab === "all-orders") {
      fetchAllOrdersFn(0);
    } else {
      fetchAllSettlementFn(0);
    }
  }, [activeTab]);

  return (
    <Navbar>
      <div className="ccs-page">
        {/* Ambient Background */}
        <div className="ccs-bg">
          <div className="ccs-bg-blob-top" />
          <div className="ccs-bg-blob-bottom" />
        </div>

        {/* Scroll Area */}
        <div className="ccs-scroll">
          <div className="ccs-inner">
            <div className="ccs-content">
              <div className="ccs-card">
                {/* ── Card Header ── */}
                <div className="ccs-card-header">
                  <div className="ccs-customer-info">
                    <div className="ccs-customer-row">
                      <button
                        type="button"
                        className="ccs-back-btn"
                        data-testid="button-credit-customer-back"
                        aria-label="Back to Customer Credit Summary"
                        onClick={() => navigate(-1)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m15 18-6-6 6-6" />
                        </svg>
                      </button>
                      <div className="ccs-customer-text">
                        <h2 data-testid="text-credit-customer-page-title">
                          {meta?.customerName ?? "—"}
                        </h2>
                        <div className="ccs-customer-meta">
                          <span data-testid="text-credit-customer-page-phone">
                            {meta?.mobileNumber ?? "—"}
                          </span>
                          <span className="ccs-meta-dot">•</span>
                          <span data-testid="text-credit-order-count">
                            {meta?.outStandingOrders ?? 0} credit orders
                          </span>
                          <span className="ccs-meta-dot">•</span>
                          <span data-testid="text-credit-last-payment-inline">
                            Last Payment:{" "}
                            {meta?.lastPaymentDate ? formatDate(meta?.lastPaymentDate) : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ccs-balance-card">
                    <div className="ccs-balance-inner">
                      <div>
                        <p className="ccs-balance-label">Open balance</p>
                        <p
                          className="ccs-balance-amount"
                          data-testid="text-credit-customer-page-open-balance"
                        >
                          {formatCurrency(meta?.outStandingAmount)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="ccs-pay-btn"
                        data-testid="button-credit-customer-pay"
                        onClick={handleOpenPayment}
                      >
                        Pay
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Tabs Section ── */}
                <div className="ccs-tabs-section">
                  <div className="ccs-tabs-wrapper">
                    {/* Tab buttons */}
                    <div className="ccs-tab-list" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "all-orders"}
                        data-testid="tab-credit-detail-all-orders"
                        className={`ccs-tab-btn ${
                          activeTab === "all-orders" ? "active-orders" : ""
                        }`}
                        onClick={() => setActiveTab("all-orders")}
                      >
                        All Orders
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "all-settlements"}
                        data-testid="tab-credit-detail-settled-orders"
                        className={`ccs-tab-btn ${
                          activeTab === "all-settlements" ? "active-settlements" : ""
                        }`}
                        onClick={() => setActiveTab("all-settlements")}
                      >
                        All Settlements
                      </button>
                    </div>

                    {/* ── All Orders Panel ── */}
                    <div
                      role="tabpanel"
                      className={`ccs-tab-panel ${activeTab === "all-orders" ? "active" : ""}`}
                    >
                      <div className="ccs-grid-container">
                        {/* Header row */}
                        <div className="ccs-grid ccs-grid-orders ccs-grid-header">
                          {ORDER_COLS?.map((col) => (
                            <div key={col} className="ccs-grid-cell ccs-grid-head-cell">
                              {col}
                            </div>
                          ))}
                        </div>

                        {/* Data rows */}
                        {loading ? (
                          Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonRow key={i} cols={8} gridClass="ccs-grid-orders" />
                          ))
                        ) : orderLogs?.length === 0 ? (
                          <EmptyState
                            gridClass="ccs-grid-orders"
                            cols={8}
                            message="No orders found"
                          />
                        ) : (
                          orderLogs?.map((log) => (
                            <div
                              key={log?.accountLogId}
                              className="ccs-grid ccs-grid-orders ccs-grid-row"
                              data-testid={`row-credit-order-${log?.accountLogId}`}
                              onClick={() => {
                                setViewBillOpen(true);
                                setSelectedSettlement(log);
                              }}
                            >
                              <div className="ccs-grid-cell ccs-td-date">
                                {formatDateOnly(log?.createdDate)}
                              </div>
                              <div className="ccs-grid-cell ccs-td-orderid">
                                {log?.orderId ?? "—"}
                              </div>
                              <div className="ccs-grid-cell ccs-td-count">{log?.items ?? "—"}</div>
                              <div className="ccs-grid-cell ccs-td-cashier">
                                {log?.cashier ?? "—"}
                              </div>
                              <div className="ccs-grid-cell ccs-td-aging">
                                {log?.invoiceAging ?? "—"}
                              </div>
                              <div className="ccs-grid-cell">
                                <Badge status={log?.status} />
                              </div>
                              <div className="ccs-grid-cell ccs-td-amount">
                                {formatCurrency(log?.orderAmount)}
                              </div>
                              <div className="ccs-grid-cell ccs-td-paydate">
                                {log?.paymentDate ? formatDate(log?.paymentDate) : "Pending"}
                              </div>
                            </div>
                          ))
                        )}

                        {/* Pagination */}
                        {!loading && (
                          <Pagination
                            page={orderPage}
                            totalPages={orderTotalPages}
                            totalResults={orderTotalResults}
                            pageSize={PAGE_SIZE}
                            onPageChange={(p) => fetchAllOrdersFn(p)}
                          />
                        )}
                      </div>
                    </div>

                    {/* ── All Settlements Panel ── */}
                    <div
                      role="tabpanel"
                      className={`ccs-tab-panel ${activeTab === "all-settlements" ? "active" : ""}`}
                    >
                      <div className="ccs-grid-container">
                        {/* Header row */}
                        <div className="ccs-grid ccs-grid-settlements ccs-grid-header">
                          {SETTLEMENT_COLS?.map((col) => (
                            <div key={col} className="ccs-grid-cell ccs-grid-head-cell">
                              {col}
                            </div>
                          ))}
                        </div>

                        {/* Data rows */}
                        {loading ? (
                          Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonRow key={i} cols={4} gridClass="ccs-grid-settlements" />
                          ))
                        ) : settlements?.length === 0 ? (
                          <EmptyState
                            gridClass="ccs-grid-settlements"
                            cols={4}
                            message="No settlements found"
                          />
                        ) : (
                          settlements?.map((s) => (
                            <div
                              key={s?.settlementId ?? s?.transactionCode}
                              className="ccs-grid ccs-grid-settlements ccs-grid-row"
                              data-testid={`row-credit-settlement-${s?.settlementId}`}
                            >
                              <div className="ccs-grid-cell ccs-td-orderid">
                                {s?.settlementId ?? "—"}
                              </div>
                              <div className="ccs-grid-cell ccs-td-amount">
                                {formatCurrency(s?.amount ?? s?.settledAmount)}
                              </div>
                              <div className="ccs-grid-cell ccs-td-date">
                                {s?.createdDate ? formatDate(s?.createdDate) : "—"}
                              </div>
                              <div className="ccs-grid-cell">
                                {s?.paymentMethod ?? "—"}
                              </div>
                            </div>
                          ))
                        )}

                        {/* Pagination */}
                        {!loading && (
                          <Pagination
                            page={settlementPage}
                            totalPages={settlementTotalPages}
                            totalResults={settlementTotalResults}
                            pageSize={PAGE_SIZE}
                            onPageChange={(p) => fetchAllSettlementFn(p)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewBillOpen && (
        <>
          <BillDrawer
            open={viewBillOpen}
            onClose={() => setViewBillOpen(false)}
            orderId={selectedSettlement?.orderId}
            orderDetails={selectedSettlement}
          />
        </>
      )}

      <PaymentModal
        variant="restaurant"
        openPayModal={paymentModalOpen}
        closePaymentModal={closePaymentModal}
        cartItems={[{ id: "credit-placeholder" }]} // modal needs non-empty array to enable Pay button
        total={tpTotal}
        showOnlyGrandTotal={true}
        isCreditPayment={true}
        orderSummary={null}
        paymentMethod={tpPaymentMethod}
        setPaymentMethod={setTpPaymentMethod}
        enterAmount={tpEnterAmount}
        setEnterAmount={setTpEnterAmount}
        enterAmountRef={tpEnterAmountRef}
        checkPayment={tpCheckPayment}
        partialCompletePayment={tpPartialCompletePayment}
        splitActive={tpSplitActive}
        setSplitActive={setTpSplitActive}
        handleSplitPayment={tpHandleSplitPayment}
        handlesplitInputChange={tpHandleSplitInputChange}
        partialStatus={tpPartialStatus}
        hideSplitPayment={true}
        setPartailStatus={setTpPartialStatus}
        amountPaid={tpAmountPaid}
        amountDue={tpAmountDue}
        cashChange={tpCashChange}
        newLoader={tpNewLoader}
        modalLoader={tpModalLoader}
        payLoader={tpPayLoader}
        paymentLoader={tpPaymentLoader}
        paymentLoadingModal={tpPaymentLoader}
        canceledOnce={false}
        paymentSuccess={tpPaymentSuccess}
        setPaymentSuccess={setTpPaymentSuccess}
        paymentFailed={tpPaymentFailed}
        paymentCancel={tpPaymentCancel}
        manual={tpManual}
        cancelLoader={tpCancelLoader}
        newId={tpNewId}
        orderId={tpNewId}
        isError={false}
        isPrinting={false}
        adjustCheck={false}
        amtToBePaid={0}
        amtHeading={"Payable Amount"}
        storeMode={"ONLINE"}
        singleOrderoffline={false}
        resetValues={tpResetValues}
        setpaymentLoader={setTpPaymentLoader}
        setOpen={() => {}}
        setPackingCharge={() => {}}
        setDeliveryCharge={() => {}}
        handleOpenPaymentModal={() => {}}
        handleOptionChange={() => {}}
        setOpenCartCustomerDetail={() => {}}
        setShowFineDineActionButtons={() => {}}
        handleManual={() => setTpManual(true)}
        handleManualorders={() => {}}
        handleTansactionCancel={() => {
          setTpPaymentFailed(false);
          setTpPaymentCancel(false);
          setTpPaymentLoader(false);
        }}
        functionCalled={false}
        setFunctionCalled={() => {}}
        cardFunctionCalled={false}
        setCardFunctionCalled={() => {}}
        splitFunctionCalled={false}
        setSplitFunctionCalled={() => {}}
        cashierPin={""}
        approveloader={false}
        transCode={""}
        newTransId={""}
        cashIcon={cashNew}
        cashIconSelected={cashNewSelected}
        upiIcon={upiNew}
        upiIconSelected={upiNewSelected}
        cardIcon={cardNew}
        cardIconSelected={cardNewSelected}
        splitImg={splitNew}
        splitImgSelected={splitNewSelected}
        clrAmountFun={() => setTpEnterAmount("")}
        setAmtFocus={() => {}}
      />
    </Navbar>
  );
};

export default CustomerCreditSummary;
